// Lockfile tab, the reproducibility manifest the compiler emits (.lock). It
// pins the source, the bound externs and the resulting address so a build can be
// reproduced byte-for-byte. Rendered as a monospace block with Copy + a
// Download .lock action named after the active contract.

import { useCallback } from "react";
import { Download, FileLock2 } from "lucide-react";
import { useIde } from "../store";
import { CopyButton, EmptyState, JsonHighlight, SectionHead, runHint } from "./shared";

/** The .lock filename for the active contract, e.g. "vault.sl" → "vault.lock". */
function lockNameFor(activePath: string | null): string {
  if (!activePath) return "contract.lock";
  const base = activePath.replace(/\.[^./]+$/, "");
  return `${base}.lock`;
}

/** Total non-empty lines, a quiet count for the section head. */
function lineCount(text: string): number {
  return text.split("\n").filter((l) => l.length > 0).length;
}

export function LockfilePanel() {
  const result = useIde((s) => s.result);
  const activePath = useIde((s) => s.activePath);
  const lockfile = result?.lockfile;

  const fileName = lockNameFor(activePath);

  const onDownload = useCallback(() => {
    if (!lockfile) return;
    const blob = new Blob([lockfile], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after the click has been dispatched.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [lockfile, fileName]);

  if (!lockfile) {
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<FileLock2 size={22} strokeWidth={1.5} />}
          title="No lockfile yet"
          hint={runHint("the reproducible build manifest (.lock)")}
        />
      </div>
    );
  }

  return (
    <div className="pn-scroll">
      <SectionHead
        title={fileName}
        count={`${lineCount(lockfile)} lines`}
        right={
          <span className="pn-lock-actions">
            <CopyButton value={lockfile} title="Copy lockfile" />
            <button
              type="button"
              className="pn-action"
              onClick={onDownload}
              title={`Download ${fileName}`}
            >
              <Download size={13} strokeWidth={1.9} />
              <span>Download</span>
            </button>
          </span>
        }
      />
      <pre className="pn-code pn-lock-pre"><JsonHighlight text={lockfile} /></pre>
    </div>
  );
}
